/**
 * Trip content helpers shared by the agency catalog, the trip detail page and
 * (later) the public shareable trip page.
 */

import type { Trip, TripTranslation } from "@common/types";
import type { Locale } from "../../stores/locale-store";

export interface TripImageView {
  src: string;
  alt: string;
}

/** The text fields a translation may override. */
export type LocalizedTrip = Trip &
  Required<Pick<TripTranslation, "name" | "description">>;

/**
 * Trip with the text fields of `locale` applied on top of the base values.
 * Missing translated fields fall back to the base (untranslated) content.
 */
export function localizeTrip(trip: Trip, locale: Locale): LocalizedTrip {
  const tr = trip.translations?.[locale];
  return {
    ...trip,
    name: tr?.name || trip.name,
    tagline: tr?.tagline || trip.tagline,
    description: tr?.description || trip.description,
    longDescription: tr?.longDescription || trip.longDescription,
    highlights: tr?.highlights?.length ? tr.highlights : trip.highlights,
    included: tr?.included?.length ? tr.included : trip.included,
    notIncluded: tr?.notIncluded?.length ? tr.notIncluded : trip.notIncluded,
    whatToBring: tr?.whatToBring?.length ? tr.whatToBring : trip.whatToBring,
    cancellationPolicy: tr?.cancellationPolicy || trip.cancellationPolicy,
  };
}

/** All images of a trip, cover first. */
export function getTripImages(trip: Trip): TripImageView[] {
  const alt = trip.imageAlt || trip.name;
  const images: TripImageView[] = [];
  if (trip.imageUrl) images.push({ src: trip.imageUrl, alt });
  for (const src of trip.galleryImages ?? []) images.push({ src, alt });
  return images;
}

/** Route segment for a trip: its slug, or the id for trips without one. */
export function tripPath(trip: Trip): string {
  return `/trips/${encodeURIComponent(trip.slug || trip.id)}`;
}

export function findTripBySlug(trips: Trip[], slug: string): Trip | undefined {
  return trips.find((t) => t.slug === slug) ?? trips.find((t) => t.id === slug);
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} h`;
  return `${hours} h ${mins} min`;
}

export function formatPrice(amount: number, locale: Locale): string {
  return amount.toLocaleString(locale === "sv" ? "sv-SE" : "en-GB");
}

/**
 * Catalog order: featured first, then by sortOrder (unset last), then name.
 */
export function compareTrips(a: Trip, b: Trip): number {
  if (!!a.isFeatured !== !!b.isFeatured) return a.isFeatured ? -1 : 1;
  const ao = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
  const bo = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
  if (ao !== bo) return ao - bo;
  return a.name.localeCompare(b.name);
}
